import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

import { usersApi } from '../services/api';
import { useAuthStore } from '../store';
import { Button, Card, Badge, Input, Select, PageHeader, Spinner, Table } from '../components/ui';

/*
|--------------------------------------------------------------------------
| Users Page (Config -> Users)
|--------------------------------------------------------------------------
| Lists every user in the system. Only admins can create, edit, or remove
| users — everyone else sees a read-only list. The role check here
| (currentUser?.role === 'admin') assumes the backend includes a `role`
| field on the logged-in user object (from GET /auth/me / the login
| response stored in useAuthStore). If the backend uses a different field
| name or a permissions array instead of a single role string, update
| `isAdmin` below to match.
|
| IMPORTANT: this is a UI-only gate. The backend must independently reject
| POST/PUT/DELETE /users calls from non-admins (e.g. 403), since a
| non-admin could otherwise call the API directly and bypass a
| frontend-only check.
|--------------------------------------------------------------------------
*/

export default function UsersPage() {

  const { user: currentUser } = useAuthStore();
  const isAdmin = currentUser?.role === 'admin';

  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const { data, isLoading, isError, error } = useQuery(
    'users',
    () => usersApi.list(),
    { refetchOnWindowFocus: false }
  );

  const users = data?.data?.data || data?.data || [];
  
  const invalidate = () => queryClient.invalidateQueries('users');

  const createMutation = useMutation(
    (body) => usersApi.create(body),
    {
      onSuccess: () => {
        toast.success('User created');
        invalidate();
        setShowCreateModal(false);
      },
      onError: (err) => toast.error(err.response?.data?.error || err.message || 'Failed to create user')
    }
  );

  const updateMutation = useMutation(
    ({ id, body }) => usersApi.update(id, body),
    {
      onSuccess: () => {
        toast.success('User updated');
        invalidate();
        setEditingUser(null);
      },
      onError: (err) => toast.error(err.response?.data?.error || err.message || 'Failed to update user')
    }
  );

  const removeMutation = useMutation(
    (userId) => usersApi.remove(userId),
    {
      onSuccess: () => { toast.success('User removed'); invalidate(); },
      onError: (err) => toast.error(err.response?.data?.error || err.message || 'Failed to remove user')
    }
  );

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              background: 'var(--accent)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 600,
              flexShrink: 0
            }}
          >
            {(row.name || row.email || '?').slice(0, 2).toUpperCase()}
          </div>
          <span>{row.name || '—'}</span>
        </div>
      )
    },
    { key: 'email', header: 'Email' },
    {
      key: 'role',
      header: 'Role',
      render: (value) => (
        <Badge variant={value === 'admin' ? 'accent' : 'neutral'}>
          {value || 'user'}
        </Badge>
      )
    },
    {
      key: 'actions',
      header: '',
      render: (_, row) => isAdmin ? (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={() => setEditingUser(row)}>
            Edit
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={row.id === currentUser?.id}
            title={row.id === currentUser?.id ? "You can't remove your own account" : 'Remove user'}
            onClick={() => {
              if (window.confirm(`Remove ${row.name || row.email}? This cannot be undone.`)) {
                removeMutation.mutate(row.id);
              }
            }}
          >
            Remove
          </Button>
        </div>
      ) : null
    }
  ];

  return (
    <div style={{ padding: 24 }}>

      <PageHeader
        title="Users"
        subtitle={
          isAdmin
            ? 'Manage who can access this app and what role they have.'
            : 'View users with access to this app. Only admins can add or edit users.'
        }
        actions={
          isAdmin ? (
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              + New User
            </Button>
          ) : null
        }
      />

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <Spinner />
        </div>
      ) : isError ? (
        <Card>
          <div style={{ color: 'var(--danger)', fontSize: 13 }}>
            Failed to load users: {error?.response?.data?.error || error?.message}
          </div>
        </Card>
      ) : (
        <Card style={{ padding: 0 }}>
          <Table columns={columns} data={users} />
        </Card>
      )}

      {showCreateModal && (
        <UserFormModal
          title="New User"
          onClose={() => setShowCreateModal(false)}
          onSave={body => createMutation.mutate(body)}
          saving={createMutation.isLoading}
        />
      )}

      {editingUser && (
        <UserFormModal
          title="Edit User"
          initialValues={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={body => updateMutation.mutate({ id: editingUser.id, body })}
          saving={updateMutation.isLoading}
        />
      )}

    </div>
  );
}

/*
|--------------------------------------------------------------------------
| User Form Modal (Create / Edit) — admin only
|--------------------------------------------------------------------------
*/

function UserFormModal({ title, initialValues, onClose, onSave, saving }) {

  const [name, setName] = useState(initialValues?.name || '');
  const [email, setEmail] = useState(initialValues?.email || '');
  const [role, setRole] = useState(initialValues?.role || 'user');
  const [password, setPassword] = useState('');

  const isEdit = !!initialValues;
  const canSave = name.trim() && email.trim() && (isEdit || password.trim());

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24
      }}
    >
      <div
        style={{
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-lg)',
          maxWidth: 420,
          width: '100%',
          boxShadow: 'var(--shadow-md)'
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '0.5px solid var(--border)',
            fontSize: 15,
            fontWeight: 600
          }}
        >
          {title}
        </div>

        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input label="Name" value={name} onChange={e => setName(e.target.value)} autoFocus />
          <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} />
          <Select label="Role" value={role} onChange={e => setRole(e.target.value)}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </Select>
          <Input
            label={isEdit ? 'New password (leave blank to keep current)' : 'Password'}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            padding: '14px 20px',
            borderTop: '0.5px solid var(--border)'
          }}
        >
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!canSave}
            loading={saving}
            onClick={() => {
              const body = { name: name.trim(), email: email.trim(), role };
              if (password.trim()) body.password = password.trim();
              onSave(body);
            }}
          >
            {isEdit ? 'Save Changes' : 'Create User'}
          </Button>
        </div>
      </div>
    </div>
  );
}
